import { Calendar, Check, Loader2, Mail, Phone, User, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import ProfilePaymentTab from './billing/ProfilePaymentTab'
import ProfileSubscriptionTab from './billing/ProfileSubscriptionTab'
import { SUBSCRIPTION_LEDE } from './billing/subscriptionPlanCopy'
import { getBuildVersionLabel } from '../lib/appEnvironment'
import { ensureUserProfileFromAuth } from '../lib/ensureUserProfileFromAuth'
import { supabase } from '../lib/supabase'
import { showError as showToastError } from '../lib/toast'

interface UserProfile {
  id: string
  email: string
  created_at: string
  email_confirmed_at: string | null
}

interface UserProfileData {
  first_name: string | null
  last_name: string | null
  phone: string | null
  email: string | null
  resume_file_path: string | null
  resume_md_path: string | null
  ai_provider: string | null
  ai_memory_file_path: string | null
  job_positions: string[] | null
  work_type: string | null
  location: string | null
  created_at: string
  updated_at: string
}

type ProfileTab = 'account' | 'subscription' | 'payment'

function tabFromHash(): ProfileTab {
  const hash = window.location.hash.replace('#', '')
  if (hash === 'subscription' || hash === 'payment') return hash
  return 'account'
}

function Profile() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [profileData, setProfileData] = useState<UserProfileData | null>(null)
  const [activeTab, setActiveTab] = useState<ProfileTab>(() => tabFromHash())

  const syncTabFromHash = useCallback(() => {
    setActiveTab(tabFromHash())
  }, [])

  useEffect(() => {
    syncTabFromHash()
    window.addEventListener('hashchange', syncTabFromHash)
    return () => window.removeEventListener('hashchange', syncTabFromHash)
  }, [syncTabFromHash])

  const setTab = (tab: ProfileTab) => {
    setActiveTab(tab)
    const hash = tab === 'account' ? '' : tab
    window.history.replaceState(null, '', hash ? `#${hash}` : window.location.pathname)
  }

  useEffect(() => {
    let cancelled = false

    const loadingTimeout = setTimeout(() => {
      if (!cancelled) {
        console.warn('[Profile] Loading timeout after 15 seconds')
        setError('Loading timeout. Please refresh the page.')
        setLoading(false)
      }
    }, 15000)

    const loadProfile = async () => {
      try {
        setLoading(true)
        setError(null)

        const { data: { user }, error: userError } = await supabase.auth.getUser()
        if (userError || !user) {
          throw new Error('Failed to load user data')
        }

        if (cancelled) return

        const authProfile: UserProfile = {
          id: user.id,
          email: user.email || 'No email',
          created_at: user.created_at || '',
          email_confirmed_at: user.email_confirmed_at,
        }
        setUserProfile(authProfile)

        const profile = await ensureUserProfileFromAuth(supabase, user)

        if (cancelled) return

        if (profile) {
          setProfileData(profile as UserProfileData)
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const errorMessage = err instanceof Error ? err.message : String(err)
          const errorMsg = errorMessage || 'Failed to load profile'
          setError(errorMsg)
          showToastError(errorMsg)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
        clearTimeout(loadingTimeout)
      }
    }

    void loadProfile()

    return () => {
      cancelled = true
      clearTimeout(loadingTimeout)
    }
  }, [])

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return 'Invalid date'
    }
  }

  const getUserInitials = () => {
    if (profileData?.first_name && profileData?.last_name) {
      return `${profileData.first_name[0]}${profileData.last_name[0]}`.toUpperCase()
    }
    if (userProfile?.email) {
      return userProfile.email[0].toUpperCase()
    }
    return 'U'
  }

  const tabs = useMemo(
    () =>
      [
        { id: 'account' as const, label: 'Account' },
        { id: 'subscription' as const, label: 'Subscription' },
        { id: 'payment' as const, label: 'Payment' },
      ] as const,
    []
  )

  const buildVersionLabel = useMemo(() => getBuildVersionLabel(), [])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-2 text-text-secondary">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading profile…</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-danger-subtle border border-danger/20 rounded-lg p-4 text-danger-strong">
          {error}
        </div>
      </div>
    )
  }

  const headerMaxWidth = activeTab === 'subscription' ? 'max-w-[960px]' : 'max-w-[640px]'

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" data-testid="profile-page">
      <div className={`p-6 pb-0 flex-shrink-0 ${headerMaxWidth}`}>
        {activeTab === 'subscription' ? (
          <>
            <p className="text-small text-text-muted mb-1">Profile › Subscription</p>
            <h1 className="text-h1 text-text-primary mb-1">Subscription</h1>
            <p className="text-body text-text-secondary max-w-[52ch]">{SUBSCRIPTION_LEDE}</p>
          </>
        ) : (
          <>
            <h1 className="text-h1 text-text-primary mb-1">Profile</h1>
            <p className="text-body text-text-secondary">Account, subscription, and billing</p>
          </>
        )}
        <nav
          className="flex gap-4 mt-6 border-b border-border-default"
          aria-label="Profile sections"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-testid={`profile-tab-${tab.id}`}
              onClick={() => setTab(tab.id)}
              className={`pb-2 px-1 border-b-2 font-semibold text-body transition-colors duration-fast ${
                activeTab === tab.id
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className={`flex-1 p-6 space-y-6 ${headerMaxWidth}`}>
        {activeTab === 'subscription' && <ProfileSubscriptionTab />}
        {activeTab === 'payment' && <ProfilePaymentTab />}

        {activeTab === 'account' && (
          <>
            <div className="bg-surface-default border border-border-default rounded-lg p-6">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-brand-primary flex items-center justify-center">
                  <span className="text-text-inverse font-bold text-2xl">{getUserInitials()}</span>
                </div>
                <div>
                  <h2 className="text-h2 text-text-primary font-semibold">
                    {profileData?.first_name && profileData?.last_name
                      ? `${profileData.first_name} ${profileData.last_name}`
                      : userProfile?.email || 'User'}
                  </h2>
                  <p className="text-body text-text-secondary">{userProfile?.email}</p>
                </div>
              </div>
            </div>

            <div className="bg-surface-default border border-border-default rounded-lg p-6 space-y-4">
              <h2 className="text-h3 text-text-primary font-semibold mb-4">Account Information</h2>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 shrink-0 text-text-muted mt-0.5" aria-hidden />
                  <div className="flex-1">
                    <div className="text-small font-medium text-text-secondary">Email</div>
                    <div className="text-body text-text-primary">{userProfile?.email}</div>
                    {userProfile?.email_confirmed_at ? (
                      <div className="flex items-center gap-1 mt-1 text-small text-brand-primary-strong">
                        <Check className="w-4 h-4 shrink-0" aria-hidden />
                        <span>Email confirmed</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 mt-1 text-small text-warning">
                        <X className="w-4 h-4 shrink-0" aria-hidden />
                        <span>Email not confirmed</span>
                      </div>
                    )}
                  </div>
                </div>

                {profileData?.first_name && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 shrink-0 text-text-muted mt-0.5" aria-hidden />
                    <div className="flex-1">
                      <div className="text-small font-medium text-text-secondary">First name</div>
                      <div className="text-body text-text-primary">{profileData.first_name}</div>
                    </div>
                  </div>
                )}

                {profileData?.last_name && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 shrink-0 text-text-muted mt-0.5" aria-hidden />
                    <div className="flex-1">
                      <div className="text-small font-medium text-text-secondary">Last name</div>
                      <div className="text-body text-text-primary">{profileData.last_name}</div>
                    </div>
                  </div>
                )}

                {profileData?.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 shrink-0 text-text-muted mt-0.5" aria-hidden />
                    <div className="flex-1">
                      <div className="text-small font-medium text-text-secondary">Phone</div>
                      <div className="text-body text-text-primary">{profileData.phone}</div>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 shrink-0 text-text-muted mt-0.5" aria-hidden />
                  <div className="flex-1">
                    <div className="text-small font-medium text-text-secondary">Account created</div>
                    <div className="text-body text-text-primary">
                      {formatDate(userProfile?.created_at || null)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {buildVersionLabel ? (
              <p
                className="text-small text-text-muted"
                data-testid="profile-app-build-version"
              >
                App version {buildVersionLabel}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

export default Profile
